import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Play, Pause, Volume2, VolumeX, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface VoiceNote {
  id: string;
  move_number: number;
  player_id: string;
  audio_file_path: string;
  duration_ms: number;
  transcription?: string;
  created_at: string;
}

interface VoiceNotePlayerProps {
  voiceNote: VoiceNote;
  playerName?: string;
  className?: string;
  compact?: boolean;
  autoPlay?: boolean;
}

export const VoiceNotePlayer: React.FC<VoiceNotePlayerProps> = ({
  voiceNote,
  playerName,
  className = "",
  compact = false,
  autoPlay = false
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const { toast } = useToast();

  const loadAudio = useCallback(async () => {
    if (audioUrl) return audioUrl;
    
    setIsLoading(true);
    try {
      const { data, error } = await supabase.storage
        .from('voice-notes')
        .download(voiceNote.audio_file_path);
      
      if (error) throw error;
      
      const url = URL.createObjectURL(data);
      setAudioUrl(url);
      return url;
    } catch (error) {
      console.error('Error loading audio:', error);
      toast({
        title: "Audio Error",
        description: "Could not load voice note.",
        variant: "destructive"
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [voiceNote.audio_file_path, audioUrl, toast]);

  const initializeAudio = useCallback(async (url: string) => {
    const audio = new Audio(url);
    audioRef.current = audio;
    
    audio.onloadedmetadata = () => {
      setDuration(audio.duration);
    };
    
    audio.ontimeupdate = () => {
      setCurrentTime(audio.currentTime);
    };
    
    audio.onended = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };
    
    audio.volume = volume;
    audio.muted = isMuted;
    
    return audio;
  }, [volume, isMuted]);

  const playPause = useCallback(async () => {
    if (!audioRef.current) {
      const url = await loadAudio();
      if (!url) return;
      
      await initializeAudio(url);
    }
    
    const audio = audioRef.current!;
    
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      try {
        await audio.play();
        setIsPlaying(true);
      } catch (error) {
        console.error('Error playing audio:', error);
        toast({
          title: "Playback Error",
          description: "Could not play voice note.",
          variant: "destructive"
        });
      }
    }
  }, [isPlaying, loadAudio, initializeAudio, toast]);

  const handleSeek = useCallback((value: number[]) => {
    if (audioRef.current && duration > 0) {
      const newTime = (value[0] / 100) * duration;
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  }, [duration]);

  const handleVolumeChange = useCallback((value: number[]) => {
    const newVolume = value[0] / 100;
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  }, []);

  const toggleMute = useCallback(() => {
    setIsMuted(!isMuted);
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
    }
  }, [isMuted]);

  const formatTime = useCallback((time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }, []);

  // Auto-play effect
  useEffect(() => {
    if (autoPlay && !isPlaying && !audioRef.current) {
      playPause();
    }
  }, [autoPlay, isPlaying, playPause]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);

  if (compact) {
    return (
      <Button
        onClick={playPause}
        disabled={isLoading}
        variant="ghost"
        size="sm"
        className={`voice-note-player-compact ${className}`}
      >
        {isLoading ? (
          <div className="w-4 h-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        ) : isPlaying ? (
          <Pause className="w-4 h-4" />
        ) : (
          <Play className="w-4 h-4" />
        )}
        <MessageSquare className="w-3 h-3 ml-1 opacity-60" />
      </Button>
    );
  }

  return (
    <Card className={`voice-note-player ${className}`}>
      <CardContent className="p-4">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-foreground">
                {playerName || 'Player'}'s thoughts
              </p>
              <p className="text-xs text-muted-foreground">
                Move {voiceNote.move_number} • {formatTime((voiceNote.duration_ms || 0) / 1000)}
              </p>
            </div>
            
            <Button
              onClick={playPause}
              disabled={isLoading}
              size="sm"
              className="bg-primary hover:bg-primary/90"
            >
              {isLoading ? (
                <div className="w-4 h-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              ) : isPlaying ? (
                <Pause className="w-4 h-4" />
              ) : (
                <Play className="w-4 h-4" />
              )}
            </Button>
          </div>

          {duration > 0 && (
            <div className="space-y-2">
              <Slider
                value={[duration > 0 ? (currentTime / duration) * 100 : 0]}
                onValueChange={handleSeek}
                max={100}
                step={1}
                className="w-full"
              />
              
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(duration)}</span>
              </div>
            </div>
          )}

          <div className="flex items-center space-x-2">
            <Button
              onClick={toggleMute}
              variant="ghost"
              size="sm"
            >
              {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </Button>
            
            <Slider
              value={[isMuted ? 0 : volume * 100]}
              onValueChange={handleVolumeChange}
              max={100}
              step={5}
              className="flex-1 max-w-20"
            />
          </div>

          {voiceNote.transcription && (
            <div className="mt-3 p-2 bg-muted rounded-md">
              <p className="text-sm text-muted-foreground italic">
                "{voiceNote.transcription}"
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};